import Cookies from "js-cookie";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Loader } from "@/atoms";
import { AccessDeniedBlankView } from "@/components";
import { permissionsData, routesUrlsData } from "@/data";
import { useAuth } from "@/hooks";
import { CurrentUserInterface } from "@/interfaces";
import { canDoHandler, logoutHandler } from "@/lib/utils";
import { ModuleType } from "@/types";

export const AuthProvider = ({ children } : { children: React.ReactNode }) => {
    const rootReducer = useSelector((state) => state as { user: CurrentUserInterface });

    const navigate = useNavigate();

    const location = useLocation();

    const auth = useAuth(!!localStorage.getItem("isAdmin"));

    const {
        fetchUserInfoHandler,
        loading,
    } = auth;

    const { pathname } = location;

    const {
        isAdmin,
        isAuthenticated,
        role,
    } = rootReducer.user;

    useEffect(
        () => {
            if (Cookies.get("accessToken") && Cookies.get("refreshToken")) fetchUserInfoHandler();
            else {
                logoutHandler(
                    pathname,
                    navigate,
                    !!localStorage.getItem("isAdmin") || pathname.includes(routesUrlsData.admin.home),
                );
            }
        },
        [pathname], // eslint-disable-line
    );

    useEffect(
        () => {
            const authRoutes = Object.values(routesUrlsData.auth);

            if (!isAuthenticated && !loading && !authRoutes.includes(pathname)) {
                logoutHandler(
                    pathname,
                    navigate,
                    pathname.includes(routesUrlsData.admin.home),
                );
            }
        },
        [isAuthenticated, loading, pathname], // eslint-disable-line
    );

    useEffect(
        () => {
            const authRoutes = Object.values(routesUrlsData.auth);

            if (isAuthenticated && authRoutes.includes(pathname)) {
                if (isAdmin) navigate(routesUrlsData.admin.home);
                else navigate(routesUrlsData.home);
            }

            if (isAuthenticated && !isAdmin && pathname.startsWith(routesUrlsData.admin.home)) navigate(routesUrlsData.home);
            else if (isAuthenticated && isAdmin && !pathname.startsWith(routesUrlsData.admin.home)) navigate(routesUrlsData.admin.home);
        },
        [isAuthenticated, pathname, isAdmin], // eslint-disable-line
    );

    if (pathname !== routesUrlsData.home && !pathname.includes(routesUrlsData.auth.path) && !pathname.includes(routesUrlsData.admin.home) && !loading) {
        const routesNeedViewAndCreate: string[] = [
            routesUrlsData.dataSources.setup,
            routesUrlsData.dataSources.offlineMapping,
        ];

        const module = pathname.
            split("/")[1].replace(
                /s$/,
                "",
            ).replace(
                /-/g,
                "_",
            ).toUpperCase() as ModuleType;

        const canView = canDoHandler(
            role,
            module,
            permissionsData.actions.view,
        );

        const requiresCreate = routesNeedViewAndCreate.some((route) => pathname.startsWith(route));

        if (!canView) return <AccessDeniedBlankView />;

        if (requiresCreate) {
            const canCreate = canDoHandler(
                role,
                module,
                permissionsData.actions.create,
            );

            if (!canCreate) return <AccessDeniedBlankView />;
        }
    }

    if (loading && Cookies.get("accessToken") && Cookies.get("refreshToken") && !isAuthenticated) {
        return (
            <div className="h-screen">
                <Loader />
            </div>
        );
    }

    return children;
};
